import {
  Alert,
  Button,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCcw, Send } from 'lucide-react';
import { type JSX, useState } from 'react';

import {
  createChangeRequest,
  listChangeRequests,
  MAX_CHANGE_REQUEST_PAGE_SIZE,
  type ChangeRequestStatus,
  type Order,
} from '../../../orders/api';
import {
  toChangeRequestFormValues,
  toRequestedChangeLines,
  type ChangeRequestFormValues,
} from '../../../orders/change-request-form-schema';
import { hasPermission } from '../../../auth/has-permission';
import type { CurrentUser } from '../../../auth/use-current-user';
import { ChangeRequestFormModal } from './change-request-form-modal';

export interface OrderChangeRequestsTabProps {
  order: Order;
  currentUser: CurrentUser | undefined;
}

const STATUS_LABELS: Record<ChangeRequestStatus, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  APPLIED: 'Aplicada',
};

/**
 * `order-change-requests/spec.md` → "Creación de solicitud de cambio
 * sobre pedido confirmado" (UI). "Solicitar cambio" habilitado solo si
 * `Order.status === 'CONFIRMED'` (`design.md` §2.6: es justamente el
 * estado en el que `isEditable() === false`, la única puerta que le
 * queda al solicitante). `GET /orders/change-requests` no acepta un
 * filtro por `orderId` (`listChangeRequestsQuerySchema`): la lista, si
 * el actor puede verla, se resuelve filtrando client-side, mismo
 * criterio que `CalendarListPage` ya aplica a `serviceTypeId`. Un
 * solicitante sin `order.approve.change` (p. ej. `CLIENT_BUYER`) no
 * puede llamar ese endpoint en absoluto (403), así que la lista ni se
 * pide para ese actor — solo el botón queda disponible.
 */
export function OrderChangeRequestsTab({
  order,
  currentUser,
}: OrderChangeRequestsTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);

  const canRequestChange =
    order.status === 'CONFIRMED' && hasPermission(currentUser, 'order.update');
  const canApprove = hasPermission(currentUser, 'order.approve.change');

  const query = useQuery({
    queryKey: ['change-requests-for-order', order.id],
    queryFn: () =>
      listChangeRequests({ status: undefined, page: 1, pageSize: MAX_CHANGE_REQUEST_PAGE_SIZE }),
    enabled: canApprove,
  });

  const requestsForOrder = (query.data?.items ?? []).filter(
    (request) => request.orderId === order.id,
  );

  const createMutation = useMutation({
    mutationFn: (values: ChangeRequestFormValues) =>
      createChangeRequest(order.id, toRequestedChangeLines(values)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['change-requests-for-order', order.id] });
      setModalOpen(false);
      toast('success', 'Solicitud de cambio enviada');
    },
  });

  return (
    <div className="flex flex-col gap-6">
      {canRequestChange ? (
        <div>
          <Button icon={Send} onClick={() => setModalOpen(true)}>
            Solicitar cambio
          </Button>
        </div>
      ) : null}

      {canApprove && query.isError ? (
        <Alert tone="danger">No pudimos cargar las solicitudes de cambio de este pedido.</Alert>
      ) : null}

      {canApprove && query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {canApprove && query.data !== undefined && requestsForOrder.length === 0 ? (
        <EmptyState icon={RefreshCcw} title="Este pedido todavía no tiene solicitudes de cambio." />
      ) : null}

      {canApprove && requestsForOrder.length > 0 ? (
        <Table>
          <caption className="sr-only">Solicitudes de cambio de este pedido</caption>
          <thead>
            <tr>
              <TableHeader>Solicitante</TableHeader>
              <TableHeader>Cambio solicitado</TableHeader>
              <TableHeader>Estado</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {requestsForOrder.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.requesterFullName ?? request.requestedBy}</TableCell>
                <TableCell>
                  {request.lineChanges.length === 0
                    ? '—'
                    : request.lineChanges
                        .map(
                          (line) => `${line.positionName}: ${line.oldQuantity} → ${line.newQuantity}`,
                        )
                        .join(', ')}
                </TableCell>
                <TableCell>{STATUS_LABELS[request.status]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modalOpen ? (
        <ChangeRequestFormModal
          serviceDate={order.serviceDate}
          defaultValues={toChangeRequestFormValues(order.items)}
          submitting={createMutation.isPending}
          error={createMutation.error}
          onClose={() => setModalOpen(false)}
          onSubmit={async (values) => {
            await createMutation.mutateAsync(values).catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}
